import { ComponentCategory, ComponentProp, DesignSystemComponent } from '../types';
import { parseComponentAST, extractClassCode, extractComponentBlock } from './ast';
import { displayNameFromPath } from '../content/util';

// Infer component category from tag name
function inferCategory(tagName: string): ComponentCategory {
	const lowerTag = tagName.toLowerCase();

	if (lowerTag.includes('button')) {
		return 'button';
	}
	if (lowerTag.includes('card')) {
		return 'card';
	}
	if (lowerTag.includes('input') || lowerTag.includes('field') || lowerTag.includes('form')) {
		return 'form';
	}
	if (
		lowerTag.includes('sidebar') ||
		lowerTag.includes('nav') ||
		lowerTag.includes('menu') ||
		lowerTag.includes('footer') ||
		lowerTag.includes('breadcrumb') ||
		lowerTag.includes('tab')
	) {
		return 'navigation';
	}
	if (
		lowerTag.includes('table') ||
		lowerTag.includes('stat') ||
		lowerTag.includes('badge') ||
		lowerTag.includes('avatar')
	) {
		return 'data-display';
	}

	return 'other';
}

// Extract props from static props declaration
// Components must declare: static props = { propName: { type, default?, options? }, ... }
function extractStaticProps(classCode: string): ComponentProp[] {
	const props: ComponentProp[] = [];

	// Find static props = { ... }
	const staticPropsMatch = classCode.match(/static\s+props\s*=\s*\{/);
	if (!staticPropsMatch || staticPropsMatch.index === undefined) {
		return props;
	}

	// Find the matching closing brace by counting depth
	const startIndex = staticPropsMatch.index + staticPropsMatch[0].length - 1;
	let depth = 0;
	let endIndex = startIndex;
	let inString = false;
	let stringChar = '';
	let isEscaped = false;
	for (let i = startIndex; i < classCode.length; i++) {
		const char = classCode[i];
		if (inString) {
			if (isEscaped) {
				isEscaped = false;
				continue;
			}
			if (char === '\\') {
				isEscaped = true;
				continue;
			}
			if (char === stringChar) {
				inString = false;
				stringChar = '';
			}
			continue;
		}
		if (char === '"' || char === "'" || char === '`') {
			inString = true;
			stringChar = char;
			continue;
		}
		if (char === '{') depth++;
		else if (char === '}') {
			depth--;
			if (depth === 0) {
				endIndex = i;
				break;
			}
		}
	}

	const propsObjectStr = classCode.substring(startIndex + 1, endIndex);

	// Parse each prop definition
	// Match: propName: { ... } or 'prop-name': { ... }
	const propRegex = /(?:['"]?([\w-]+)['"]?)\s*:\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g;
	let propMatch;

	while ((propMatch = propRegex.exec(propsObjectStr)) !== null) {
		const propName = propMatch[1];
		const propDefStr = propMatch[2];

		const prop: ComponentProp = {
			name: propName,
			type: 'string',
		};

		// Extract type
		const typeMatch = propDefStr.match(/type\s*:\s*['"](\w+)['"]/);
		if (typeMatch) {
			const typeValue = typeMatch[1] as ComponentProp['type'];
			if (['string', 'number', 'boolean', 'select', 'json', 'icon'].includes(typeValue)) {
				prop.type = typeValue;
			}
		}

		// Extract default value - try different patterns
		// JSON defaults need special handling: they're typically single-quoted strings
		// containing double quotes, e.g. default: '[{"key":"val"}]'
		if (prop.type === 'json') {
			// Match single-quoted string (can contain double quotes and escapes)
			const jsonSingleQuote = propDefStr.match(/default\s*:\s*'((?:[^'\\]|\\.)*)'/);
			// Match double-quoted string (can contain single quotes and escapes)
			const jsonDoubleQuote = propDefStr.match(/default\s*:\s*"((?:[^"\\]|\\.)*)"/);
			if (jsonSingleQuote) {
				prop.defaultValue = jsonSingleQuote[1];
			} else if (jsonDoubleQuote) {
				// Unescape the captured content by parsing the full quoted string
				try {
					prop.defaultValue = JSON.parse('"' + jsonDoubleQuote[1] + '"');
				} catch {
					prop.defaultValue = jsonDoubleQuote[1];
				}
			}
		} else {
			const stringDefault = propDefStr.match(/default\s*:\s*['"]([^'"]*)['"]/);
			const numberDefault = propDefStr.match(/default\s*:\s*(\d+(?:\.\d+)?)/);
			const boolDefault = propDefStr.match(/default\s*:\s*(true|false)/);

			if (stringDefault) {
				prop.defaultValue = stringDefault[1];
			} else if (boolDefault) {
				prop.defaultValue = boolDefault[1];
			} else if (numberDefault) {
				prop.defaultValue = numberDefault[1];
			}
		}

		// Extract options for select type
		if (prop.type === 'select') {
			const optionsMatch = propDefStr.match(/options\s*:\s*\[([^\]]+)\]/);
			if (optionsMatch) {
				const optionsStr = optionsMatch[1];
				const options: string[] = [];
				const optionRegex = /['"]([^'"]+)['"]/g;
				let optMatch;
				while ((optMatch = optionRegex.exec(optionsStr)) !== null) {
					options.push(optMatch[1]);
				}
				if (options.length > 0) {
					prop.options = options;
				}
			}
		}

		// Extract required flag
		const requiredMatch = propDefStr.match(/required\s*:\s*(true|false)/);
		if (requiredMatch && requiredMatch[1] === 'true') {
			prop.required = true;
		}

		// Extract description
		const descMatch = propDefStr.match(/description\s*:\s*['"]([^'"]+)['"]/);
		if (descMatch) {
			prop.description = descMatch[1];
		}

		props.push(prop);
	}

	return props;
}

// Generate example HTML from component
function generateExampleHTML(tagName: string, props: ComponentProp[]): string {
	const attributes: string[] = [];

	props.forEach((prop) => {
		if (prop.defaultValue !== undefined) {
			if (prop.type === 'json') {
				try {
					const parsed = JSON.parse(prop.defaultValue);
					attributes.push(`${prop.name}='${JSON.stringify(parsed)}'`);
				} catch {
					attributes.push(`${prop.name}="${prop.defaultValue}"`);
				}
			} else if (prop.type === 'boolean') {
				if (prop.defaultValue === 'true') {
					attributes.push(prop.name);
				}
			} else {
				attributes.push(`${prop.name}="${prop.defaultValue}"`);
			}
		} else if (!prop.required) {
			// Add example value for non-required props
			if (prop.type === 'string') {
				attributes.push(`${prop.name}="Example"`);
			} else if (prop.type === 'number') {
				attributes.push(`${prop.name}="0"`);
			} else if (prop.type === 'icon') {
				attributes.push(`${prop.name}="star"`);
			}
		}
	});

	const attrsString = attributes.length > 0 ? ' ' + attributes.join(' ') : '';
	return `<${tagName}${attrsString}></${tagName}>`;
}

export function parseComponents(
	jsString: string,
	cssMap?: Map<string, string>,
	exampleMap?: Map<string, { props?: Record<string, any>; children?: string }>,
	guidelinesMap?: Map<string, string>
): DesignSystemComponent[] {
	const components: DesignSystemComponent[] = [];

	// Parse using AST
	const parsed = parseComponentAST(jsString);

	// Process each component block
	for (const block of parsed.blocks) {
		const tagName = block.tagName;

		const classCode = extractClassCode(block, jsString);
		const sourceCode = extractComponentBlock(block, jsString);

		const props = extractStaticProps(classCode);

		const category = inferCategory(tagName);
		const exampleHTML = generateExampleHTML(tagName, props);

		// Extract variants and sizes from props
		const variantProp = props.find((p) => p.name === 'variant' && p.type === 'select');
		const sizeProp = props.find((p) => p.name === 'size' && p.type === 'select');

		// Match CSS file to component by tagName
		// e.g., "ds-button" -> "components/button/button.css"
		let cssCode: string | undefined;
		if (cssMap) {
			const componentName = tagName.replace(/^ds-/, '');
			// Try multiple possible paths
			const possiblePaths = [
				`components/${componentName}/${componentName}.css`,
				`components/${componentName}/index.css`,
			];
			for (const path of possiblePaths) {
				if (cssMap.has(path)) {
					cssCode = cssMap.get(path);
					break;
				}
			}
		}

		// Look up example data and guidelines for this component
		const exampleData = exampleMap?.get(tagName);
		const guidelines = guidelinesMap?.get(tagName);

		const component: DesignSystemComponent = {
			id: tagName,
			name: displayNameFromPath(`components/${tagName.replace(/^ds-/, '')}/${tagName}.js`, 'components'),
			tagName,
			category,
			props,
			exampleHTML,
			exampleProps: exampleData?.props,
			children: exampleData?.children,
			variants: variantProp?.options,
			sizes: sizeProp?.options,
			sourceCode,
			cssCode,
			...(guidelines && { guidelines }),
		};

		components.push(component);
	}

	return components;
}
